import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import BrandsSettings from '../components/settings/BrandsSettings';
import AgencyBrandingSettings from '../components/settings/AgencyBrandingSettings';
import { resolveLimits } from '@traaker/shared';
import type {
  User,
  UserRole,
  Tenant,
  BillingStatus,
  BillingMode,
  FeatureFlags,
  TierLimits,
} from '@traaker/shared';

const ROLE_LABELS: Record<UserRole, string> = {
  owner: 'Proprietaire',
  admin: 'Administrateur',
  member: 'Membre',
  viewer: 'Lecteur',
};

const ROLE_COLORS: Record<UserRole, string> = {
  owner: '#10B981',
  admin: '#7C3AED',
  member: '#5F6470',
  viewer: '#A0A4AE',
};

const TAB_KEYS = ['org', 'brands', 'users', 'branding'] as const;
type TabKey = (typeof TAB_KEYS)[number];

export default function Settings() {
  const { tenant, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState<User[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  // Only agency tenants (reportsEnabled) get to see the white-label tab.
  const reportsEnabled = !!tenant?.settings?.featureFlags?.reportsEnabled;

  const tabParam = (searchParams.get('tab') || 'org') as TabKey;
  const tabOrder: TabKey[] = reportsEnabled
    ? ['org', 'brands', 'users', 'branding']
    : ['org', 'brands', 'users'];
  const activeIdx = Math.max(0, tabOrder.indexOf(tabParam));
  const activeTab = tabOrder[activeIdx];

  const handleTabChange = (_: unknown, value: number) => {
    const key = tabOrder[value];
    setSearchParams(key === 'org' ? {} : { tab: key });
  };

  const isAdmin = user?.role === 'owner' || user?.role === 'admin';

  useEffect(() => {
    api.get<User[]>('/users').then(setUsers).catch(() => {});
  }, []);

  const handleRoleChange = async (userId: string, role: UserRole) => {
    try {
      await api.patch(`/users/${userId}/role`, { role });
      setUsers((prev) => prev.map((u) => (u.userId === userId ? { ...u, role } : u)));
    } catch {
      setMessage('Erreur lors du changement de role');
    }
  };

  const handleRemoveUser = async (userId: string) => {
    try {
      await api.delete(`/users/${userId}`);
      setUsers((prev) => prev.filter((u) => u.userId !== userId));
    } catch {
      setMessage('Erreur lors de la suppression');
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        Parametres
      </Typography>

      {message && (
        <Alert severity="info" onClose={() => setMessage(null)} sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      <Tabs
        value={activeIdx}
        onChange={handleTabChange}
        sx={{ mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Tab label="Entreprise" />
        <Tab label="Marques & Localisations" />
        <Tab label="Utilisateurs" />
        {reportsEnabled && <Tab label="Marque blanche" />}
      </Tabs>

      {/* Tab: Organization */}
      {activeTab === 'org' && tenant && <EnterpriseTab tenant={tenant} />}

      {/* Tab: Brands & Locations */}
      {activeTab === 'brands' && <BrandsSettings />}

      {/* Tab: Agency branding (reports white-label) */}
      {activeTab === 'branding' && reportsEnabled && <AgencyBrandingSettings />}

      {/* Tab: Users */}
      {activeTab === 'users' && (
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Utilisateurs ({users.length})
              </Typography>
              {isAdmin && (
                <Button
                  startIcon={<PersonAddIcon />}
                  variant="outlined"
                  size="small"
                  onClick={() => setInviteOpen(true)}
                >
                  Inviter
                </Button>
              )}
            </Box>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nom</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  {isAdmin && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.userId}>
                    <TableCell>{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      {isAdmin && u.role !== 'owner' ? (
                        <Select
                          value={u.role}
                          size="small"
                          onChange={(e) => handleRoleChange(u.userId, e.target.value as UserRole)}
                          sx={{ minWidth: 130 }}
                        >
                          {(['admin', 'member', 'viewer'] as UserRole[]).map((r) => (
                            <MenuItem key={r} value={r}>{ROLE_LABELS[r]}</MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <Chip
                          label={ROLE_LABELS[u.role]}
                          size="small"
                          sx={{ bgcolor: `${ROLE_COLORS[u.role]}20`, color: ROLE_COLORS[u.role], fontWeight: 600 }}
                        />
                      )}
                    </TableCell>
                    {isAdmin && (
                      <TableCell align="right">
                        {u.role !== 'owner' && (
                          <IconButton size="small" onClick={() => handleRemoveUser(u.userId)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <InviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={(u) => {
          setUsers((prev) => [...prev, u]);
          setInviteOpen(false);
        }}
      />
    </Box>
  );
}

const TIER_LABELS: Record<string, string> = {
  lite: 'Lite',
  pro: 'Pro',
  enterprise: 'Enterprise',
};

const BILLING_STATUS_LABELS: Record<BillingStatus, { label: string; color: 'default' | 'success' | 'warning' | 'error' }> = {
  pending: { label: 'En attente de paiement', color: 'warning' },
  active: { label: 'Active', color: 'success' },
  past_due: { label: 'Paiement en retard', color: 'warning' },
  suspended: { label: 'Suspendue', color: 'error' },
  cancelled: { label: 'Annulée', color: 'default' },
};

const BILLING_MODE_LABELS: Record<BillingMode, string> = {
  stripe: 'Stripe',
  manual: 'Facturation manuelle',
};

const FEATURE_FLAG_LABELS: Record<keyof FeatureFlags, string> = {
  tasksEnabled: 'Tâches',
  newsWatchEnabled: 'Veille news',
  competitorsEnabled: 'Concurrents',
  reportsEnabled: 'Rapports',
};

// Subset of TierLimits shown in the quotas card, ordered + labelled.
const QUOTA_ROWS: ReadonlyArray<{ key: keyof TierLimits; label: string; unit?: string }> = [
  { key: 'brands', label: 'Marques' },
  { key: 'zones', label: 'Zones / localisations' },
  { key: 'users', label: 'Utilisateurs' },
  { key: 'competitors', label: 'Concurrents' },
  { key: 'scansPerMonth', label: 'Scans / mois' },
  { key: 'apiCallsPerDay', label: 'Appels API / jour' },
  { key: 'concurrentAgents', label: 'Agents concurrents' },
  { key: 'brandNarrativeQueries', label: 'Requêtes Brand Narrative' },
  { key: 'brandAuthorityQueries', label: 'Requêtes Brand Authority' },
  { key: 'brandReferralQueriesPerLevel', label: 'Requêtes Brand Referral (par niveau)' },
  { key: 'brandNarrativeRunsPerMonth', label: 'Runs Brand Narrative / mois' },
  { key: 'brandAuthorityRunsPerMonth', label: 'Runs Brand Authority / mois' },
  { key: 'brandReferralRunsPerMonth', label: 'Runs Brand Referral / mois' },
  { key: 'socialIntegrations', label: 'Intégrations sociales' },
  { key: 'newsWatchMaxProposals', label: 'Max propositions veille / brand' },
];

function formatLimit(value: unknown): string {
  if (typeof value === 'number') return value === -1 ? 'Illimité' : String(value);
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
  if (typeof value === 'string') return value;
  return '—';
}

function formatAmount(cents?: number, interval?: 'month' | 'year'): string {
  if (cents == null) return '—';
  const amount = (cents / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
  if (interval) return `${amount} / ${interval === 'month' ? 'mois' : 'an'}`;
  return amount;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Box sx={{ textAlign: 'right' }}>{children}</Box>
    </Box>
  );
}

function EnterpriseTab({ tenant }: { tenant: Tenant }) {
  const billingLabel = BILLING_STATUS_LABELS[tenant.billingStatus] ?? { label: tenant.billingStatus, color: 'default' as const };
  const limits = resolveLimits(tenant.tier, tenant.settings?.quotaOverrides);
  const overrideKeys = Object.keys(tenant.settings?.quotaOverrides ?? {}) as Array<keyof TierLimits>;
  const featureFlags = tenant.settings?.featureFlags ?? {};
  const featureEntries = (Object.keys(FEATURE_FLAG_LABELS) as Array<keyof FeatureFlags>).map((k) => ({
    key: k,
    label: FEATURE_FLAG_LABELS[k],
    enabled: !!featureFlags[k],
  }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Identity — read only */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Entreprise
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Ces informations sont gérées par votre administrateur Traaker. Contactez-nous pour toute modification.
          </Alert>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Nom"
              value={tenant.name}
              disabled
              size="small"
              sx={{ flex: 1, minWidth: 200 }}
            />
            <TextField
              label="Domaine"
              value={tenant.domain}
              disabled
              size="small"
              sx={{ flex: 1, minWidth: 200 }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Subscription */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Abonnement
          </Typography>
          <InfoRow label="Offre">
            <Chip label={TIER_LABELS[tenant.tier] ?? tenant.tier} size="small" sx={{ fontWeight: 600 }} />
          </InfoRow>
          <InfoRow label="Statut de facturation">
            <Chip label={billingLabel.label} color={billingLabel.color} size="small" variant="outlined" />
          </InfoRow>
          <InfoRow label="Mode de facturation">
            <Typography variant="body2">{BILLING_MODE_LABELS[tenant.billingMode]}</Typography>
          </InfoRow>
          {tenant.contractAmountCents != null && (
            <InfoRow label="Montant du contrat">
              <Typography variant="body2" fontWeight={600}>
                {formatAmount(tenant.contractAmountCents, tenant.contractInterval)}
              </Typography>
            </InfoRow>
          )}
          <InfoRow label="Client depuis">
            <Typography variant="body2">{formatDate(tenant.createdAt)}</Typography>
          </InfoRow>
          {tenant.contractNotes && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Notes</Typography>
              <Typography variant="body2">{tenant.contractNotes}</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Activated features */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
            Modules activés
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Modules supplémentaires activables par votre administrateur Traaker.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {featureEntries.map((f) => (
              <Chip
                key={f.key}
                label={f.label}
                size="small"
                color={f.enabled ? 'success' : 'default'}
                variant={f.enabled ? 'filled' : 'outlined'}
              />
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Quotas & limits */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
            Quotas & limites
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Valeurs effectives pour votre offre, y compris les ajustements spécifiques à votre contrat.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ressource</TableCell>
                <TableCell align="right">Limite</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {QUOTA_ROWS.map((row) => {
                const overridden = overrideKeys.includes(row.key);
                return (
                  <TableRow key={row.key}>
                    <TableCell>
                      {row.label}
                      {overridden && (
                        <Chip label="Personnalisé" size="small" color="info" variant="outlined" sx={{ ml: 1 }} />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {formatLimit(limits[row.key])}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow>
                <TableCell>Suggestions de contenu</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600}>
                    {limits.contentSuggestions ? 'Incluses' : 'Non incluses'}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Accès webhook / API</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600}>
                    {limits.webhookAccess ? 'Inclus' : 'Non inclus'}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Mode de crawl</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600}>
                    {limits.crawlMode === 'full' ? 'Complet' : 'Sitemap'}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Types d'audit disponibles</TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{limits.auditTypes.length}</Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

    </Box>
  );
}

function InviteDialog({
  open,
  onClose,
  onInvited,
}: {
  open: boolean;
  onClose: () => void;
  onInvited: (user: User) => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<UserRole>('member');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    try {
      const user = await api.post<User>('/users/invite', { email, name, role });
      onInvited(user);
      setEmail('');
      setName('');
      setRole('member');
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Inviter un utilisateur</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField label="Nom" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
        <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth />
        <Select value={role} onChange={(e) => setRole(e.target.value as UserRole)} fullWidth>
          {(['admin', 'member', 'viewer'] as UserRole[]).map((r) => (
            <MenuItem key={r} value={r}>{ROLE_LABELS[r]}</MenuItem>
          ))}
        </Select>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!email || !name}>
          Inviter
        </Button>
      </DialogActions>
    </Dialog>
  );
}
